
import React, { useState, useEffect } from 'react';
import { Plus, X, Check, Clock, BookOpen, AlertCircle, ChevronLeft, ChevronRight, Calendar, List } from 'lucide-react';

const styles = {
  backgroundContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    padding: '16px',
    margin: '0 auto',
    maxWidth: '100%'
  },
  todoContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  todoHeader: {
    marginBottom: '24px'
  },
  todoTitle: {
    margin: '0 0 16px 0',
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1f2937'
  },
  stats: {
    display: 'flex',
    gap: '16px',
    fontSize: '14px',
    color: '#6b7280',
    flexWrap: 'wrap'
  },
  addTaskSection: {
    backgroundColor: '#f9fafb',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '24px'
  },
  addTaskForm: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px',
    flexWrap: 'wrap'
  },
  input: {
    padding: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '16px',
    minWidth: '200px',
    flex: 1,
    boxSizing: 'border-box'
  },
  select: {
    padding: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '16px',
    backgroundColor: 'white',
    boxSizing: 'border-box'
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    whiteSpace: 'nowrap'
  },
  filterSection: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  filterButton: {
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  filterActive: {
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6'
  },
  filterInactive: {
    backgroundColor: 'white',
    color: '#374151'
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#9ca3af',
    fontSize: '16px'
  },
  taskItem: {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px'
  },
  taskItemCompleted: {
    opacity: '0.7',
    backgroundColor: '#f9fafb'
  },
  taskContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  checkbox: {
    width: '20px',
    height: '20px',
    border: '2px solid #d1d5db',
    borderRadius: '4px',
    backgroundColor: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  checkboxCompleted: {
    backgroundColor: '#10b981',
    borderColor: '#10b981',
    color: 'white'
  },
  taskText: {
    flex: 1,
    fontSize: '16px',
    lineHeight: '1.5'
  },
  taskTextCompleted: {
    textDecoration: 'line-through',
    color: '#9ca3af'
  },
  taskMeta: {
    fontSize: '12px',
    color: '#9ca3af',
    marginTop: '4px'
  },
  deleteButton: {
    padding: '4px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  progressSection: {
    marginTop: '24px',
    padding: '20px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px'
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#10b981',
    transition: 'width 0.3s ease'
  },
  // Calendar specific styles
  calendarContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden'
  },
  calendarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb'
  },
  calendarTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937'
  },
  navButton: {
    padding: '8px',
    border: 'none',
    backgroundColor: 'transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    color: '#374151'
  },
  weekDays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    backgroundColor: '#f3f4f6'
  },
  weekDay: {
    padding: '8px 4px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '600',
    color: '#374151',
    borderRight: '1px solid #e5e7eb'
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    backgroundColor: 'white'
  },
  calendarDay: {
    minHeight: '70px',
    padding: '6px 4px',
    border: '1px solid #f3f4f6',
    cursor: 'pointer',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '12px'
  },
  calendarDayNumber: {
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '2px'
  },
  otherMonth: {
    color: '#d1d5db'
  },
  today: {
    backgroundColor: '#dbeafe',
    borderColor: '#3b82f6'
  },
  selectedDay: {
    backgroundColor: '#3b82f6',
    color: 'white'
  },
  taskIndicator: {
    fontSize: '9px',
    padding: '1px 2px',
    borderRadius: '2px',
    marginBottom: '1px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    lineHeight: '1.2'
  },
  completedTask: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },
  pendingTask: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },
  overdueTask: {
    backgroundColor: '#fecaca',
    color: '#991b1b'
  },
  viewToggle: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  viewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    backgroundColor: 'white'
  },
  viewButtonActive: {
    backgroundColor: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6'
  }
};

// Mobile-specific responsive styles
const getMobileStyles = (isMobile, isSmallMobile) => ({
  backgroundContainer: {
    ...styles.backgroundContainer,
    padding: isMobile ? '8px' : '16px'
  },
  todoContainer: {
    ...styles.todoContainer,
    padding: isMobile ? '8px' : '16px'
  },
  todoTitle: {
    ...styles.todoTitle,
    fontSize: isMobile ? (isSmallMobile ? '24px' : '24px') : '24px'
  },
  stats: {
    ...styles.stats,
    gap: isMobile ? '8px' : '16px',
    fontSize: isMobile ? '12px' : '14px'
  },
  addTaskSection: {
    ...styles.addTaskSection,
    padding: isMobile ? '12px' : '16px'
  },
  addTaskForm: {
    ...styles.addTaskForm,
    flexDirection: isMobile ? 'column' : 'row'
  },
  input: {
    ...styles.input,
    minWidth: isMobile ? '100%' : '200px',
    fontSize: '16px' // Prevent zoom on iOS
  },
  select: {
    ...styles.select,
    width: isMobile ? '100%' : 'auto',
    fontSize: '16px'
  },
  addButton: {
    ...styles.addButton,
    width: isMobile ? '100%' : 'auto',
    justifyContent: 'center'
  },
  taskContent: {
    ...styles.taskContent,
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'flex-start',
    gap: isMobile ? '8px' : '12px'
  },
  taskContentInner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    width: '100%'
  },
  calendarDay: {
    ...styles.calendarDay,
    minHeight: isMobile ? (isSmallMobile ? '50px' : '60px') : '70px',
    padding: isMobile ? '3px 2px' : '6px 4px'
  },
  calendarDayNumber: {
    ...styles.calendarDayNumber,
    fontSize: isMobile ? '11px' : '12px'
  },
  taskIndicator: {
    ...styles.taskIndicator,
    fontSize: isMobile ? '8px' : '9px',
    padding: '1px',
    display: isMobile && isSmallMobile ? 'none' : 'block'
  },
  weekDay: {
    ...styles.weekDay,
    padding: isMobile ? '6px 2px' : '8px 4px',
    fontSize: isMobile ? '10px' : '12px'
  },
  calendarHeader: {
    ...styles.calendarHeader,
    padding: isMobile ? '12px 16px' : '16px 20px'
  },
  calendarTitle: {
    ...styles.calendarTitle,
    fontSize: isMobile ? '16px' : '18px'
  },
  viewToggle: {
    ...styles.viewToggle,
    flexDirection: isMobile ? 'column' : 'row'
  },
  viewButton: {
    ...styles.viewButton,
    justifyContent: isMobile ? 'center' : 'flex-start',
    fontSize: isMobile ? '12px' : '14px'
  },
  mobileTaskActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
    justifyContent: 'flex-end'
  },
  headerContainer: {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'flex-start' : 'center',
    marginBottom: '16px',
    gap: isMobile ? '12px' : '0'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  reminderSection: {
    display: 'flex',
    gap: isMobile ? '8px' : '12px',
    flexWrap: 'wrap',
    alignItems: 'end',
    marginTop: '8px'
  },
  reminderInput: {
    ...styles.input,
    width: isMobile ? '60px' : '70px',
    minWidth: 'unset'
  },
  textareaStyle: {
    ...styles.input,
    width: '100%',
    minWidth: isMobile ? '100%' : '500px',
    height: isMobile ? '60px' : '50px',
    resize: 'none',
    fontFamily: 'inherit'
  }
});

// Calendar Component
function CalendarView({ tasks, onDateSelect, selectedDate, isMobile, isSmallMobile }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const mobileStyles = getMobileStyles(isMobile, isSmallMobile);
  
  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  // Get first day of month and last day
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());
  
  // Generate calendar days
  const days = [];
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    days.push(date);
  }
  
  const navigateMonth = (direction) => {
    setCurrentDate(new Date(year, month + direction, 1));
  };
  
  const getTasksForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return tasks.filter(task => {
      if (!task.deadline) return false;
      return task.deadline.split('T')[0] === dateStr;
    });
  };
  
  const isToday = (date) => {
    return date.toDateString() === today.toDateString();
  };
  
  const isSelected = (date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };
  
  const isCurrentMonth = (date) => {
    return date.getMonth() === month;
  };
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div style={styles.calendarContainer}>
      <div style={mobileStyles.calendarHeader}>
        <button 
          onClick={() => navigateMonth(-1)}
          style={styles.navButton}
        >
          <ChevronLeft size={20} />
        </button>
        <h3 style={mobileStyles.calendarTitle}>{formatDate(currentDate)}</h3>
        <button 
          onClick={() => navigateMonth(1)}
          style={styles.navButton}
        >
          <ChevronRight size={20} />
        </button>
      </div>
      
      <div style={styles.weekDays}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={mobileStyles.weekDay}>{day}</div>
        ))}
      </div>
      
      <div style={styles.calendarGrid}>
        {days.map((date, index) => {
          const dayTasks = getTasksForDate(date);
          const completedTasks = dayTasks.filter(t => t.completed);
          const pendingTasks = dayTasks.filter(t => !t.completed);
          const overdueTasks = pendingTasks.filter(t => new Date() > new Date(t.deadline));
          const maxTasksToShow = isSmallMobile ? 0 : (isMobile ? 1 : 2);
          
          return (
            <div
              key={index}
              style={{
                ...mobileStyles.calendarDay,
                ...(isToday(date) ? styles.today : {}),
                ...(isSelected(date) ? styles.selectedDay : {}),
                ...(isSelected(date) ? { color: 'white' } : {})
              }}
              onClick={() => onDateSelect(date)}
            >
              <div style={{
                ...mobileStyles.calendarDayNumber,
                ...(isCurrentMonth(date) ? {} : styles.otherMonth),
                ...(isSelected(date) ? { color: 'white' } : {})
              }}>
                {date.getDate()}
              </div>
              
              {/* Task indicators */}
              {!isSmallMobile && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  {overdueTasks.slice(0, maxTasksToShow).map((task, i) => (
                    <div key={`overdue-${i}`} style={{...mobileStyles.taskIndicator, ...styles.overdueTask}}>
                      ⚠ {task.text}
                    </div>
                  ))}
                  {pendingTasks.filter(t => !overdueTasks.includes(t)).slice(0, maxTasksToShow - overdueTasks.length).map((task, i) => (
                    <div key={`pending-${i}`} style={{...mobileStyles.taskIndicator, ...styles.pendingTask}}>
                      📝 {task.text}
                    </div>
                  ))}
                  {completedTasks.slice(0, maxTasksToShow - Math.min(maxTasksToShow, pendingTasks.length)).map((task, i) => (
                    <div key={`completed-${i}`} style={{...mobileStyles.taskIndicator, ...styles.completedTask}}>
                      ✅ {task.text}
                    </div>
                  ))}
                  {dayTasks.length > maxTasksToShow && maxTasksToShow > 0 && (
                    <div style={{...mobileStyles.taskIndicator, backgroundColor: '#e5e7eb', color: '#374151'}}>
                      +{dayTasks.length - maxTasksToShow}
                    </div>
                  )}
                </div>
              )}
              
              {/* Simple dot indicator for small mobile */}
              {isSmallMobile && dayTasks.length > 0 && (
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: overdueTasks.length > 0 ? '#ef4444' : 
                                 pendingTasks.length > 0 ? '#f59e0b' : '#10b981',
                  alignSelf: 'center',
                  marginTop: 'auto'
                }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Main Todo List Component
function TodoList() {
  const [tasks, setTasks] = useState([]);
  const [activeReminders, setActiveReminders] = useState([]);
  const [view, setView] = useState('calendar'); // 'calendar' or 'list'
  const [selectedDate, setSelectedDate] = useState(null);
  const [newTask, setNewTask] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPriority, setNewPriority] = useState('medium');
  const [newDeadline, setNewDeadline] = useState('');
  const [newReminderValue, setNewReminderValue] = useState(1);
  const [newReminderUnit, setNewReminderUnit] = useState('days');
  const [filter, setFilter] = useState('all');
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const mobileStyles = getMobileStyles(isMobile, isSmallMobile);

  // Additional styles specific to clear data button
  const clearDataButtonStyle = {
    padding: '6px 12px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: isMobile ? '11px' : '12px',
    marginLeft: isMobile ? '0' : '8px',
    marginTop: isMobile ? '4px' : '0'
  };

  // Clear all saved data function
  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all saved todo data? This action cannot be undone.')) {
      try {
        // Reset all state (removing localStorage for artifacts)
        setTasks([]);
        setActiveReminders([]);
        setNewTask('');
        setNewDescription('');
        setNewPriority('medium');
        setNewDeadline('');
        setNewReminderValue(1);
        setNewReminderUnit('days');
        setFilter('all');
        setSelectedDate(null);
        
        alert('All todo data cleared successfully!');
      } catch (error) {
        console.error('Error clearing data:', error);
        alert('Error clearing data. Please try again.');
      }
    }
  };

  const calculateReminderTime = (deadline, reminderValue, reminderUnit) => {
    if (!deadline || !reminderValue) return null;
    
    const deadlineDate = new Date(deadline);
    const reminderMs = {
      minutes: reminderValue * 60 * 1000,
      hours: reminderValue * 60 * 60 * 1000,
      days: reminderValue * 24 * 60 * 60 * 1000,
      weeks: reminderValue * 7 * 24 * 60 * 60 * 1000
    };
    
    return new Date(deadlineDate.getTime() - reminderMs[reminderUnit]).toISOString();
  };

  const isTaskOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date() > new Date(deadline);
  };

  const isReminderActive = (task) => {
    if (!task.reminderTime || task.completed || task.reminderTriggered) return false;
    const now = Date.now();
    const reminderTime = new Date(task.reminderTime).getTime();
    
    // Show reminder icon if we're within 24 hours of reminder time
    return (now >= reminderTime - (24 * 60 * 60 * 1000)) && (now < reminderTime);
  };

  const isReminderTriggered = (task) => {
    return task.reminderTriggered && !task.completed;
  };

  // addTask with enhanced reminder system
  const addTask = () => {
    if (newTask.trim()) {
      const task = {
        id: Date.now(),
        text: newTask.trim(),
        description: newDescription.trim(),
        completed: false,
        priority: newPriority,
        deadline: newDeadline || null,
        reminderValue: newReminderValue,
        reminderUnit: newReminderUnit,
        reminderTime: newDeadline
          ? calculateReminderTime(newDeadline, newReminderValue, newReminderUnit)
          : null,
        reminderTriggered: false, // Track if reminder was shown
        createdAt: new Date().toLocaleDateString()
      };
      setTasks([task, ...tasks]);
      setNewTask('');
      setNewDescription('');
      setNewDeadline('');
    }
  };

  const toggleTask = (id) => {
    setTasks(tasks.map(task =>
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
  };

  const deleteTask = (id) => {
    setTasks(tasks.filter(task => task.id !== id));
    setActiveReminders(prev => prev.filter(reminderId => reminderId !== id));
  };

  // Reset reminder for a task (useful for testing or re-enabling)
  const resetReminder = (id) => {
    setTasks(tasks.map(task =>
      task.id === id ? { ...task, reminderTriggered: false } : task
    ));
  };

  const getPriorityIcon = (priority) => {
    const iconStyle = { width: '16px', height: '16px' };
    switch (priority) {
      case 'high': return <AlertCircle style={{...iconStyle, color: '#ef4444'}} />;
      case 'medium': return <Clock style={{...iconStyle, color: '#f59e0b'}} />;
      case 'low': return <BookOpen style={{...iconStyle, color: '#10b981'}} />;
      default: return null;
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setView('list');
  };

  // Filter tasks based on selected date and filter type
  const getFilteredTasks = () => {
    let filtered = tasks;
    
    // Filter by selected date if in list view and date is selected
    if (selectedDate && view === 'list') {
      const dateStr = selectedDate.toISOString().split('T')[0];
      filtered = tasks.filter(task => {
        if (!task.deadline) return false;
        return task.deadline.split('T')[0] === dateStr;
      });
    }
    
    // Apply completion filter
    if (filter === 'completed') return filtered.filter(t => t.completed);
    if (filter === 'pending') return filtered.filter(t => !t.completed);
    return filtered;
  };

  const filteredTasks = getFilteredTasks();

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.completed).length,
    pending: tasks.filter(t => !t.completed).length,
    reminders: tasks.filter(t => t.reminderTime && !t.reminderTriggered && !t.completed).length,
    triggered: tasks.filter(t => t.reminderTriggered && !t.completed).length
  };

  return (
    <div style={mobileStyles.backgroundContainer}>
      <div style={mobileStyles.todoContainer}>
        <div style={styles.contentCard}>
          <div style={styles.todoHeader}>
            <div style={mobileStyles.headerContainer}>
              <h2 style={mobileStyles.todoTitle}>📅 Calendar To-do List</h2>
              <div style={mobileStyles.headerActions}>
                <span style={{fontSize: isMobile ? '11px' : '12px', color: '#10b981'}}>✓ Auto-saved</span>
                <button onClick={clearAllData} style={clearDataButtonStyle}>
                  🗑️ Clear All Data
                </button>
              </div>
            </div>
            <div style={mobileStyles.stats}>
              <span>Total: {stats.total}</span>
              <span style={{color: '#10b981'}}>Completed: {stats.completed}</span>
              <span style={{color: '#f59e0b'}}>Pending: {stats.pending}</span>
              <span style={{color: '#8b5cf6'}}>Active Reminders: {stats.reminders}</span>
            </div>
          </div>

          {/* View Toggle */}
          <div style={mobileStyles.viewToggle}>
            <button
              onClick={() => setView('calendar')}
              style={{
                ...mobileStyles.viewButton,
                ...(view === 'calendar' ? styles.viewButtonActive : {})
              }}
            >
              <Calendar size={16} />
              Calendar View
            </button>
            <button
              onClick={() => setView('list')}
              style={{
                ...mobileStyles.viewButton,
                ...(view === 'list' ? styles.viewButtonActive : {})
              }}
            >
              <List size={16} />
              List View
              {selectedDate && ` - ${selectedDate.toLocaleDateString()}`}
            </button>
            {selectedDate && view === 'list' && (
              <button
                onClick={() => {setSelectedDate(null); setView('calendar');}}
                style={{...mobileStyles.viewButton, backgroundColor: '#f3f4f6'}}
              >
                <X size={16} />
                Clear Date Filter
              </button>
            )}
          </div>

          {/* Calendar View */}
          {view === 'calendar' && (
            <CalendarView
              tasks={tasks}
              onDateSelect={handleDateSelect}
              selectedDate={selectedDate}
            />
          )}

          {/* List View */}
          {view === 'list' && (
            <>
              <div style={styles.addTaskSection}>
                <div style={styles.addTaskForm}>
                  <input
                    type="text"
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    placeholder="Add a new task..."
                    style={styles.input}
                    onKeyPress={(e) => e.key === 'Enter' && addTask()}
                  />
                  <select
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value)}
                    style={styles.select}
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                  <button onClick={addTask} style={styles.addButton}>
                    <Plus style={{width: '16px', height: '16px'}} />
                    Add
                  </button>
                </div>
                
                <div style={{marginBottom: '12px'}}>
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Add description (optional)..."
                    style={{
                      ...styles.input,
                      width: '100%',
                      minWidth: '500px',
                      height: '50px',
                  resize: 'none',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'end'}}>
              <div style={{flex: 1, minWidth: '200px'}}>
                <label style={{display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px'}}>
                  Deadline (optional)
                </label>
                <input
                  type="datetime-local"
                  value={newDeadline}
                  onChange={(e) => setNewDeadline(e.target.value)}
                  style={styles.input}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
              
              {newDeadline && (
                <div style={{display: 'flex', gap: '8px', alignItems: 'end'}}>
                  <div>
                    <label style={{display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px'}}>
                      Remind me
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={newReminderValue}
                      onChange={(e) => setNewReminderValue(parseInt(e.target.value) || 1)}
                      style={{...styles.input, width: '70px'}}
                    />
                  </div>
                  <div>
                    <select
                      value={newReminderUnit}
                      onChange={(e) => setNewReminderUnit(e.target.value)}
                      style={styles.select}
                    >
                      <option value="minutes">min before</option>
                      <option value="hours">hrs before</option>
                      <option value="days">days before</option>
                      <option value="weeks">weeks before</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={styles.filterSection}>
            {['all', 'pending', 'completed'].map((filterType) => (
              <button
                key={filterType}
                onClick={() => setFilter(filterType)}
                style={{
                  ...styles.filterButton,
                  ...(filter === filterType ? styles.filterActive : styles.filterInactive)
                }}
              >
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
              </button>
            ))}
          </div>
<div style={styles.taskList}>
            {filteredTasks.length === 0 ? (
              <div style={styles.emptyState}>
                {filter === 'all' ? 
                  (selectedDate ? 
                    `No tasks scheduled for ${selectedDate.toLocaleDateString()}` : 
                    'No tasks yet. Add one above!'
                  ) : 
                  `No ${filter} tasks${selectedDate ? ` for ${selectedDate.toLocaleDateString()}` : ''}.`
                }
              </div>
            ) : (
              filteredTasks.map((task) => {
                const overdue = isTaskOverdue(task.deadline);
                const reminderActive = isReminderActive(task);
                const reminderTriggered = isReminderTriggered(task);
                
                return (
                <div
                  key={task.id}
                  style={{
                    ...styles.taskItem,
                    ...(task.completed ? styles.taskItemCompleted : {}),
                    ...(overdue && !task.completed ? {backgroundColor: '#fef2f2', borderColor: '#fecaca'} : {}),
                    ...(reminderTriggered ? {backgroundColor: '#fffbeb', borderColor: '#fed7aa'} : {}),
                    ...(reminderActive ? {backgroundColor: '#f0f9ff', borderColor: '#bae6fd'} : {})
                  }}
                >
                  <div style={styles.taskContent}>
                    <button
                      onClick={() => toggleTask(task.id)}
                      style={{
                        ...styles.checkbox,
                        ...(task.completed ? styles.checkboxCompleted : {})
                      }}
                    >
                      {task.completed && <Check style={{width: '16px', height: '16px'}} />}
                    </button>
                    
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      {getPriorityIcon(task.priority)}
                      {overdue && !task.completed && <span style={{color: '#ef4444', fontSize: '16px'}} title="Overdue">⚠️</span>}
                      {reminderTriggered && <span style={{color: '#f59e0b', fontSize: '16px'}} title="Reminder triggered">🔔</span>}
                      {reminderActive && <span style={{color: '#3b82f6', fontSize: '16px'}} title="Reminder coming soon">⏰</span>}
                    </div>

                    <div style={styles.taskText}>
                      <span style={task.completed ? styles.taskTextCompleted : {}}>
                        {task.text}
                      </span>
                      
                      {task.description && (
                        <div style={{
                          fontSize: '14px',
                          color: '#6b7280',
                          marginTop: '4px',
                          lineHeight: '1.4'
                        }}>
                          {task.description}
                        </div>
                      )}
                      
                      <div style={styles.taskMeta}>
                        <span>Added: {task.createdAt} • {task.priority} priority</span>
                        {task.deadline && (
                          <span style={{
                            color: overdue && !task.completed ? '#ef4444' : '#6b7280'
                          }}>
                            {' • '}Deadline: {new Date(task.deadline).toLocaleString()}
                          </span>
                        )}
                        {task.reminderTime && (
                          <span style={{
                            color: task.reminderTriggered ? '#f59e0b' : '#6b7280'
                          }}>
                            {' • '}Reminder: {task.reminderValue} {task.reminderUnit} before
                            {task.reminderTriggered && ' (triggered)'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{display: 'flex', gap: '4px'}}>
                      {task.reminderTriggered && !task.completed && (
                        <button
                          onClick={() => resetReminder(task.id)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title="Reset reminder"
                        >
                          🔄
                        </button>
                      )}
                      <button
                        onClick={() => deleteTask(task.id)}
                        style={styles.deleteButton}
                      >
                        <X style={{width: '16px', height: '16px'}} />
                      </button>
                    </div>
                  </div>
                </div>
              )})
            )}
          </div>

          {tasks.length > 0 && (
            <div style={styles.progressSection}>
              <div style={styles.progressContainer}>
                <span>
                  {stats.completed} of {stats.total} tasks completed
                  {stats.reminders > 0 && ` • ${stats.reminders} active reminders`}
                  {stats.triggered > 0 && ` • ${stats.triggered} reminders triggered`}
                </span>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </div>
    </div>
  );
}

export default TodoList;